import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, getDay, addMonths, subMonths, isWeekend } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ClipboardList, ChevronLeft, ChevronRight, Users, Loader2,
  CalendarDays, Briefcase, BookOpen, XCircle, Coffee, AlertTriangle,
  FileText, Download, Filter
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ACTIVITY_TYPES, ALL_ACTIVITY_TYPES, LEGACY_ACTIVITY_TYPES } from '../lib/activites';
import { getAuthToken } from '../lib/authToken';
import { ActiviteCollaborateurPanel } from '../components/ActiviteCollaborateurPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const TYPE_LABELS = {
  dossier: { label: 'Dossier', short: 'D', color: 'bg-indigo-600 text-white', light: 'bg-indigo-50 text-indigo-700' },
  ...Object.fromEntries(ALL_ACTIVITY_TYPES.map(a => [a.value, { label: a.label, short: a.short, color: `${a.bg} text-white`, light: `${a.light} ${a.text}` }])),
  non_affecte: { label: 'Non affecté', short: '?', color: 'bg-red-500 text-white', light: 'bg-red-50 text-red-700' },
  weekend: { label: 'Weekend', short: '', color: 'bg-slate-200 text-slate-400', light: 'bg-slate-50 text-slate-300' },
};

const SOUS_CATEGORIES = [
  { value: 'preparation_cours', label: 'Préparation de cours' },
  { value: 'formation_interne', label: 'Formation interne' },
  { value: 'reunion_pedagogique', label: 'Réunion pédagogique' },
  { value: 'veille_technique', label: 'Veille technique' },
  { value: 'ingenierie_pedagogique', label: 'Ingénierie pédagogique' },
  { value: 'autre', label: 'Autre' },
];

export const RequetesPage = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [customRange, setCustomRange] = useState(false);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [affectations, setAffectations] = useState([]);
  const [recapitulatif, setRecapitulatif] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFormateur, setFilterFormateur] = useState('all');
  const [filterContrat, setFilterContrat] = useState('all');

  // Dialog for HP affectation
  const [hpDialogOpen, setHpDialogOpen] = useState(false);
  const [hpData, setHpData] = useState({ formateur_id: '', date: '', type_affectation: 'conge_paye', sous_categorie: '', note: '' });
  const [hpFormateurName, setHpFormateurName] = useState('');
  const [saving, setSaving] = useState(false);

  // Bulk dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkData, setBulkData] = useState({ formateur_id: '', date_debut: '', date_fin: '', type_affectation: 'conge_paye', sous_categorie: '', note: '' });
  const [bulkFormateurName, setBulkFormateurName] = useState('');

  const getPeriod = useCallback(() => {
    if (customRange && dateDebut && dateFin) {
      return { debut: dateDebut, fin: dateFin };
    }
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return { debut: format(start, 'yyyy-MM-dd'), fin: format(end, 'yyyy-MM-dd') };
  }, [currentMonth, customRange, dateDebut, dateFin]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { debut, fin } = getPeriod();
    try {
      const [affRes, recapRes] = await Promise.all([
        axios.get(`${API_URL}/requetes/affectations?date_debut=${debut}&date_fin=${fin}`),
        axios.get(`${API_URL}/requetes/recapitulatif?date_debut=${debut}&date_fin=${fin}`),
      ]);
      setAffectations(affRes.data);
      setRecapitulatif(recapRes.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [getPeriod]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { debut, fin } = getPeriod();
  const days = eachDayOfInterval({ start: parseISO(debut), end: parseISO(fin) });

  const openHpDialog = (formateurId, formateurName, date, existing) => {
    setHpFormateurName(formateurName);
    setHpData({
      formateur_id: formateurId,
      date: date,
      type_affectation: existing?.type_affectation || 'conge_paye',
      sous_categorie: existing?.sous_categorie || '',
      note: existing?.note || '',
    });
    setHpDialogOpen(true);
  };

  const openBulkDialog = (formateurId, formateurName) => {
    setBulkFormateurName(formateurName);
    const { debut: d, fin: f } = getPeriod();
    setBulkData({
      formateur_id: formateurId,
      date_debut: d,
      date_fin: f,
      type_affectation: 'conge_paye',
      sous_categorie: '',
      note: '',
    });
    setBulkDialogOpen(true);
  };

  const handleSaveHp = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/requetes/affectation-hp`, hpData);
      toast.success('Affectation enregistrée');
      setHpDialogOpen(false);
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleDeleteHp = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/requetes/affectation-hp/${hpData.formateur_id}/${hpData.date}`);
      toast.success('Affectation supprimée');
      setHpDialogOpen(false);
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleBulkSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/requetes/affectation-hp/bulk`, bulkData);
      toast.success(`${res.data.count} jours affectés`);
      setBulkDialogOpen(false);
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // Filtering
  const filtered = affectations.filter(f => {
    if (filterFormateur !== 'all' && f.formateur_id !== filterFormateur) return false;
    if (filterContrat === 'salarie' && !f.est_salarie) return false;
    if (filterContrat === 'vacataire' && f.est_salarie) return false;
    return true;
  });

  // Colonnes du récapitulatif : tous les types saisissables + anciens types encore présents dans les données
  // Colonnes du récapitulatif : uniquement les activités réellement présentes sur la période consultée
  const recapTypes = ALL_ACTIVITY_TYPES.filter(a => recapitulatif.some(r => (r.jours_par_type || {})[a.value]));
  const HJ = 7;
  const joursH = (n) => n ? `${n} (${n * HJ} h)` : '—';
  const [detailCollab, setDetailCollab] = useState(null); // collaborateur ouvert en détail depuis le récapitulatif
  const filteredRecap = recapitulatif.filter(f => {
    if (filterFormateur !== 'all' && f.formateur_id !== filterFormateur) return false;
    if (filterContrat === 'salarie' && !f.est_salarie) return false;
    if (filterContrat === 'vacataire' && f.est_salarie) return false;
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="requetes-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Requêtes</h1>
            <p className="text-sm text-slate-500">Gestion des affectations et suivi RH</p>
          </div>
        </div>

        <Tabs defaultValue="affectations">
          <TabsList>
            <TabsTrigger value="affectations" data-testid="tab-affectations">
              <CalendarDays className="w-4 h-4 mr-1" /> Affectations collaborateurs
            </TabsTrigger>
            <TabsTrigger value="recapitulatif" data-testid="tab-recapitulatif">
              <FileText className="w-4 h-4 mr-1" /> Récapitulatif
            </TabsTrigger>
          </TabsList>

          {/* ============ AFFECTATIONS TAB ============ */}
          <TabsContent value="affectations">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 space-y-4">
                {/* Controls */}
                <div className="flex flex-wrap items-center gap-3">
                  {!customRange ? (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="prev-month">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm font-semibold capitalize min-w-[140px] text-center">
                        {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                      </span>
                      <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="next-month">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="h-8 text-xs w-36" />
                      <span className="text-xs text-slate-400">au</span>
                      <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="h-8 text-xs w-36" />
                      <Button variant="outline" size="sm" onClick={fetchData} className="h-8 text-xs">Appliquer</Button>
                    </div>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setCustomRange(!customRange)} className="text-xs">
                    {customRange ? 'Vue mensuelle' : 'Période personnalisée'}
                  </Button>

                  <div className="ml-auto flex items-center gap-2">
                    <Select value={filterContrat} onValueChange={setFilterContrat}>
                      <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="salarie">Salariés</SelectItem>
                        <SelectItem value="vacataire">Vacataires</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterFormateur} onValueChange={setFilterFormateur}>
                      <SelectTrigger className="h-8 text-xs w-[180px]"><SelectValue placeholder="Collaborateur..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les collaborateurs</SelectItem>
                        {affectations.map(f => (
                          <SelectItem key={f.formateur_id} value={f.formateur_id}>{f.nom} {f.prenom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {Object.entries(TYPE_LABELS).filter(([k]) => k !== 'weekend').map(([key, val]) => (
                    <div key={key} className="flex items-center gap-1.5">
                      <span className={`w-5 h-5 rounded text-[9px] font-bold flex items-center justify-center ${val.color}`}>{val.short}</span>
                      <span className="text-slate-600">{val.label}</span>
                    </div>
                  ))}
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">Aucun collaborateur trouvé</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="sticky left-0 bg-slate-50 z-10 text-left px-2 py-2 font-semibold text-slate-700 min-w-[160px] border-r">Collaborateur</th>
                          {days.map(day => {
                            const isWe = isWeekend(day);
                            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                            return (
                              <th key={format(day, 'yyyy-MM-dd')} className={cn(
                                "text-center px-0.5 py-1 min-w-[34px] font-normal",
                                isWe && "bg-slate-100",
                                isToday && "bg-indigo-100 font-semibold"
                              )}>
                                <div className="text-[9px] text-slate-400 uppercase">{format(day, 'EEE', { locale: fr }).charAt(0)}</div>
                                <div className={cn("text-[10px]", isToday ? "text-indigo-700 font-bold" : "text-slate-600")}>{format(day, 'd')}</div>
                              </th>
                            );
                          })}
                          <th className="text-center px-2 py-2 font-semibold text-slate-700 min-w-[40px] border-l">Act.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filtered.map(f => {
                          const fullName = `${f.nom} ${f.prenom}`;
                          return (
                            <tr key={f.formateur_id} className="hover:bg-slate-50/50" data-testid={`formateur-row-${f.formateur_id}`}>
                              <td className="sticky left-0 bg-white z-10 px-2 py-1.5 border-r">
                                <div className="flex items-center gap-2">
                                  <div>
                                    <span className="font-medium text-slate-900">{f.nom} {f.prenom}</span>
                                    <span className={cn("ml-1.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full",
                                      f.est_salarie ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                                    )}>
                                      {f.type_contrat?.toUpperCase() || '—'}
                                    </span>
                                  </div>
                                  {f.est_salarie && (
                                    <button
                                      type="button"
                                      onClick={() => openBulkDialog(f.formateur_id, fullName)}
                                      className="ml-auto text-slate-400 hover:text-indigo-600"
                                      title="Affectation en masse"
                                    >
                                      <CalendarDays className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              {days.map(day => {
                                const dateStr = format(day, 'yyyy-MM-dd');
                                const jour = f.jours[dateStr];
                                if (!jour) return <td key={dateStr} className="bg-slate-50"></td>;
                                
                                const isWe = jour.is_weekend;
                                const statut = jour.statut;
                                const info = TYPE_LABELS[statut] || TYPE_LABELS.non_affecte;
                                const isNonAffecte = statut === 'non_affecte' && f.est_salarie;
                                const hasDossiers = jour.dossiers?.length > 0;
                                const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
                                
                                const tooltip = hasDossiers
                                  ? jour.dossiers.map(d => d.dossier_code).join(', ')
                                  : jour.hors_production?.note || info.label;

                                return (
                                  <td key={dateStr} className={cn("text-center p-0.5", isWe && "bg-slate-50", isToday && "bg-indigo-50/50")}>
                                    {isWe ? (
                                      <div className="w-full h-6 rounded bg-slate-100" />
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (!hasDossiers) {
                                            openHpDialog(f.formateur_id, fullName, dateStr, jour.hors_production);
                                          }
                                        }}
                                        className={cn(
                                          "w-full h-6 rounded text-[9px] font-bold flex items-center justify-center transition-all",
                                          hasDossiers ? "cursor-default" : "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-indigo-300",
                                          info.color,
                                          isNonAffecte && "animate-pulse"
                                        )}
                                        title={tooltip}
                                        data-testid={`cell-${f.formateur_id}-${dateStr}`}
                                      >
                                        {hasDossiers ? (jour.dossiers.length > 1 ? `${jour.dossiers.length}D` : 'D') : info.short}
                                      </button>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="text-center px-2 border-l">
                                {(() => {
                                  const rec = recapitulatif.find(r => r.formateur_id === f.formateur_id);
                                  const nonAff = rec?.jours_non_affectes || 0;
                                  return f.est_salarie && nonAff > 0 ? (
                                    <span className="text-red-600 font-bold" title={`${nonAff} jour(s) non affecté(s)`}>
                                      <AlertTriangle className="w-3.5 h-3.5 inline" /> {nonAff}
                                    </span>
                                  ) : (
                                    <span className="text-emerald-600 font-medium" title="Tout est renseigné">OK</span>
                                  );
                                })()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ RECAPITULATIF TAB ============ */}
          <TabsContent value="recapitulatif">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">Récapitulatif — {customRange ? `${dateDebut} au ${dateFin}` : format(currentMonth, 'MMMM yyyy', { locale: fr })}</h3>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="recap-table">
                      <thead>
                        <tr className="bg-slate-50 border-b">
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Collaborateur</th>
                          <th className="text-center px-2 py-2 font-semibold text-slate-700">Contrat</th>
                          <th className="text-center px-2 py-2 font-semibold text-indigo-700">Dossier</th>
                          {recapTypes.map(a => <th key={a.value} className={`text-center px-2 py-2 font-semibold ${a.text}`} title={a.label}>{a.short}</th>)}
                          <th className="text-center px-2 py-2 font-semibold text-red-700">Non aff.</th>
                          <th className="text-center px-2 py-2 font-semibold text-slate-700">Total ouv.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredRecap.map(r => (
                          <tr key={r.formateur_id} className="hover:bg-slate-50" data-testid={`recap-row-${r.formateur_id}`}>
                            <td className="px-3 py-2">
                              <button type="button" className="font-medium text-indigo-700 hover:underline text-left" onClick={() => setDetailCollab(r)} data-testid={`recap-nom-${r.formateur_id}`} title="Voir le détail de l'activité">{r.nom} {r.prenom}</button>
                            </td>
                            <td className="text-center px-2 py-2">
                              <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full",
                                r.est_salarie ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                              )}>
                                {r.type_contrat?.toUpperCase() || '—'}
                              </span>
                            </td>
                            <td className="text-center px-2 py-2 font-bold text-indigo-700">{joursH(r.jours_dossier)}</td>
                            {recapTypes.map(a => <td key={a.value} className={`text-center px-2 py-2 ${a.text}`} data-testid={`recap-${a.value}-${r.formateur_id}`}>{joursH((r.jours_par_type || {})[a.value] || 0)}</td>)}
                            <td className="text-center px-2 py-2">
                              {r.est_salarie && r.jours_non_affectes > 0 ? (
                                <span className="text-red-600 font-bold">{r.jours_non_affectes}</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="text-center px-2 py-2 font-semibold">{r.total_jours_ouvrables}</td>
                          </tr>
                        ))}
                      </tbody>
                      {filteredRecap.length > 0 && (
                        <tfoot>
                          <tr className="bg-slate-100 border-t-2 font-semibold">
                            <td className="px-3 py-2">Total</td>
                            <td></td>
                            <td className="text-center px-2 py-2 text-indigo-700">{joursH(filteredRecap.reduce((s, r) => s + r.jours_dossier, 0))}</td>
                            {recapTypes.map(a => <td key={a.value} className={`text-center px-2 py-2 ${a.text}`}>{joursH(filteredRecap.reduce((acc, r) => acc + ((r.jours_par_type || {})[a.value] || 0), 0))}</td>)}
                            <td className="text-center px-2 py-2 text-red-600">{filteredRecap.reduce((s, r) => s + r.jours_non_affectes, 0)}</td>
                            <td className="text-center px-2 py-2">{filteredRecap.reduce((s, r) => s + r.total_jours_ouvrables, 0)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ============ DETAIL ACTIVITE COLLABORATEUR (depuis le recapitulatif) ============ */}
        <Dialog open={!!detailCollab} onOpenChange={(o) => { if (!o) { setDetailCollab(null); fetchData(); } }}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="recap-detail-dialog">
            {detailCollab && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-indigo-600 text-white text-sm font-bold flex items-center justify-center">{(detailCollab.prenom?.[0] || '') + (detailCollab.nom?.[0] || '')}</span>
                    <span>
                      <span className="block text-lg">{detailCollab.nom} {detailCollab.prenom}</span>
                      <span className={cn("inline-block mt-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full", detailCollab.est_salarie ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700")}>{detailCollab.type_contrat?.toUpperCase() || 'SANS CONTRAT'}</span>
                    </span>
                  </DialogTitle>
                  <DialogDescription>Activité du mois consulté — chaque modification est enregistrée immédiatement ; le récapitulatif se met à jour à la fermeture.</DialogDescription>
                </DialogHeader>
                <ActiviteCollaborateurPanel formateurId={detailCollab.formateur_id} initialMonth={customRange ? parseISO(dateDebut) : currentMonth} />
                <DialogFooter>
                  <Button type="button" onClick={() => { setDetailCollab(null); fetchData(); }} data-testid="recap-detail-close">Fermer et actualiser</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ============ HP AFFECTATION DIALOG ============ */}
        <Dialog open={hpDialogOpen} onOpenChange={setHpDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
                Affectation hors-dossier
              </DialogTitle>
              <DialogDescription>
                {hpFormateurName} — {hpData.date && format(parseISO(hpData.date), 'EEEE d MMMM yyyy', { locale: fr })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Type d'affectation</Label>
                <Select value={hpData.type_affectation} onValueChange={v => setHpData({ ...hpData, type_affectation: v, sous_categorie: v !== 'developpement_pedagogique' ? '' : hpData.sous_categorie })}>
                  <SelectTrigger data-testid="hp-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map(a => <SelectItem key={a.value} value={a.value}><span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${a.bg}`} />{a.label}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {hpData.type_affectation === 'developpement_pedagogique' && (
                <div className="space-y-2">
                  <Label>Sous-catégorie</Label>
                  <Select value={hpData.sous_categorie || ''} onValueChange={v => setHpData({ ...hpData, sous_categorie: v })}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent>
                      {SOUS_CATEGORIES.map(sc => (
                        <SelectItem key={sc.value} value={sc.value}>{sc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Note</Label>
                <Input
                  value={hpData.note || ''}
                  onChange={e => setHpData({ ...hpData, note: e.target.value })}
                  placeholder="Note optionnelle..."
                  data-testid="hp-note-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              {hpData.type_affectation && (
                <Button type="button" variant="destructive" size="sm" onClick={handleDeleteHp} disabled={saving} className="mr-auto">
                  Supprimer
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setHpDialogOpen(false)}>Annuler</Button>
              <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveHp} disabled={saving} data-testid="hp-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ============ BULK HP DIALOG ============ */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
                Affectation en masse
              </DialogTitle>
              <DialogDescription>
                {bulkFormateurName} — Appliquer sur une plage de dates (jours ouvrés uniquement)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date début</Label>
                  <Input type="date" value={bulkData.date_debut} onChange={e => setBulkData({ ...bulkData, date_debut: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Input type="date" value={bulkData.date_fin} onChange={e => setBulkData({ ...bulkData, date_fin: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Type d'affectation</Label>
                <Select value={bulkData.type_affectation} onValueChange={v => setBulkData({ ...bulkData, type_affectation: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {bulkData.type_affectation === 'developpement_pedagogique' && (
                <div className="space-y-2">
                  <Label>Sous-catégorie</Label>
                  <Select value={bulkData.sous_categorie || ''} onValueChange={v => setBulkData({ ...bulkData, sous_categorie: v })}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent>
                      {SOUS_CATEGORIES.map(sc => (
                        <SelectItem key={sc.value} value={sc.value}>{sc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Note</Label>
                <Input value={bulkData.note || ''} onChange={e => setBulkData({ ...bulkData, note: e.target.value })} placeholder="Note optionnelle..." />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>Annuler</Button>
              <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleBulkSave} disabled={saving} data-testid="bulk-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
